import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TimelineScrubber = ({ data, currentIndex, onIndexChange, isPlaying, onPlayPause }) => {
  const [isDragging, setIsDragging] = useState(false);
  const scrubberRef = useRef(null);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updatePosition(e?.clientX);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updatePosition(e?.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    updatePosition(e?.touches?.[0]?.clientX);
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      updatePosition(e?.touches?.[0]?.clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const updatePosition = (clientX) => {
    if (scrubberRef?.current) {
      const rect = scrubberRef?.current?.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect?.left, rect?.width));
      const percentage = x / rect?.width;
      const newIndex = Math.round(percentage * (data?.length - 1));
      onIndexChange(newIndex);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  const progressPercentage = (currentIndex / (data?.length - 1)) * 100;

  return (
    <div className="flex flex-col gap-3 md:gap-4">
      <div className="flex items-center gap-3 md:gap-4">
        <button
          onClick={onPlayPause}
          className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90 hover:scale-105"
          aria-label={isPlaying ? 'Pause animation' : 'Play animation'}
        >
          <Icon name={isPlaying ? 'Pause' : 'Play'} size={20} color="currentColor" />
        </button>

        <div className="flex-1 relative">
          <div
            ref={scrubberRef}
            className="relative h-2 bg-muted rounded-full cursor-pointer"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div
              className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all duration-150"
              style={{ width: `${progressPercentage}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 bg-primary rounded-full shadow-lg cursor-grab active:cursor-grabbing transition-transform duration-150 hover:scale-110"
              style={{ left: `calc(${progressPercentage}% - 8px)` }}
            />
          </div>

          <div className="flex justify-between mt-2">
            <span className="text-xs md:text-sm text-muted-foreground">
              {data?.[0]?.date || 'Start'}
            </span>
            <span className="text-xs md:text-sm text-muted-foreground">
              {data?.[data?.length - 1]?.date || 'End'}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-sm md:text-base font-medium text-foreground">
          {data?.[currentIndex]?.date || 'N/A'}
        </span>
        <span className="text-sm md:text-base text-muted-foreground">
          {currentIndex + 1} / {data?.length}
        </span>
      </div>
    </div>
  );
};

export default TimelineScrubber;